import { Query, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";
import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import {
  IChannel,
  IDesk,
  IPerson,
  SmsConsentResult,
  T_DesktopInbox,
  T_EmailThread,
  T_Message,
} from "dtos/communication-channel.dto";
import { T_MongoId } from "types/_utils.types";
import { useDebounce } from "hooks/use-debounce";

type T_SendMessageBody = {
  channelId: string;
  contact?: string;
  text?: string;
  subject?: string;
  html?: string;
  threadId?: string;
};

export type T_CreateGsuiteChannelBody = {
  serviceAccountJson: {
    client_email: string;
    private_key: string;
    private_key_id: string;
    project_id: string;
    client_id: string;
  };
  impersonateEmails: string[];
};

type DeskDto = {
  _id: T_MongoId;
  correlation_id: string;
  channels: T_MongoId[];
  meta: Record<string, any>;
  lastSyncedAt?: Date | null;
};

export const communicationChannelService = {
  useCreateDesk: () => {
    return useMutation({
      mutationKey: ["desks", "create"],
      mutationFn: async (body: { correlation_id: string; meta?: Record<string, any> }) => {
        const res = await api.post("/comms/desks", body);

        return res.data as T_Api_Success_Res<{ _id: string }>;
      },
    });
  },
  useGetAllDesks: () => {
    return useQuery({
      queryKey: ["get-all-desks"],
      queryFn: async () => {
        const res = await api.get(`/comms/desks`);
        return res.data as T_Api_Success_Res<IDesk[]>;
      },
    });
  },
  // useGetDeskByID: ({ desk_id, refetchInterval }: { desk_id: string | null; refetchInterval?: number | false }) => {
  //   return useQuery({
  //     queryKey: ["desks", desk_id],
  //     queryFn: async () => {
  //       const res = await api.get(`/desks/${desk_id}`);
  //       return res.data as T_Api_Success_Res<IDesk>; // was { _id, channels } — missing lastSyncedAt
  //     },
  //     enabled: !!desk_id,
  //     refetchInterval,
  //   });
  // },
  useGetDeskByID: ({
    desk_id,
    refetchInterval,
  }: {
    desk_id: string | null;
    refetchInterval?: number | false | ((query: Query<T_Api_Success_Res<IDesk>>) => number | false | undefined);
  }) => {
    return useQuery({
      queryKey: ["desks", desk_id],
      queryFn: async () => {
        const res = await api.get(`/comms/desks/${desk_id}`);
        return res.data as T_Api_Success_Res<IDesk>;
      },
      enabled: !!desk_id,
      refetchInterval,
    });
  },
  useUpdateDesk: () => {
    return useMutation({
      mutationKey: ["desks", "update"],
      mutationFn: async ({
        desk_id,
        body,
      }: {
        desk_id: string;
        body: {
          defaultEmailChannelId?: string;
          defaultSmsChannelId?: string;
        };
      }) => {
        const res = await api.patch(`/comms/desks/${desk_id}`, body);

        return res.data as T_Api_Success_Res<IDesk>;
      },
    });
  },
  useDeleteChannel: () => {
    return useMutation({
      mutationKey: ["desks", "channels", "delete"],
      mutationFn: async ({ desk_id, channel_id }: { desk_id: string; channel_id: string }) => {
        const res = await api.delete(`/comms/desks/${desk_id}/channels/${channel_id}`);

        return res.data as T_Api_Success_Res<unknown>;
      },
    });
  },
  useUpdateChannelAlias: () => {
    return useMutation({
      mutationKey: ["desks", "channels", "alias", "update"],
      mutationFn: async ({
        desk_id,
        channel_id,
        body,
      }: {
        desk_id: string;
        channel_id: string;
        body: {
          alias: string;
        };
      }) => {
        const res = await api.patch(`/comms/desks/${desk_id}/channels/${channel_id}/alias`, body);

        return res.data as T_Api_Success_Res<IChannel>;
      },
    });
  },
  useCreateChannel: () => {
    return useMutation({
      mutationKey: ["desks", "channels", "create"],
      mutationFn: async ({ desk_id, body }: { desk_id: string; body: any }) => {
        const res = await api.post(`/comms/desks/${desk_id}/channels`, body);

        return res.data as T_Api_Success_Res<IChannel>;
      },
    });
  },
  useAddGsuiteFromExisting: () => {
    return useMutation({
      mutationKey: ["desks", "channels", "gsuite", "from-existing"],
      mutationFn: async ({
        desk_id,
        body,
      }: {
        desk_id: string;
        body: { sourceChannelId: string; impersonateEmail: string; alias?: string | null };
      }) => {
        const res = await api.post(`/comms/desks/${desk_id}/channels/gsuite/from-existing`, body);
        return res.data as T_Api_Success_Res<IChannel>;
      },
    });
  },
  useGetDesk: ({ desk_id }: { desk_id: string }) => {
    return useQuery({
      queryKey: ["desks", desk_id],
      queryFn: async () => {
        const res = await api.get(`/comms/desks/${desk_id}`);

        return res.data as T_Api_Success_Res<IDesk>;
      },
      enabled: !!desk_id,
    });
  },
  useGetInboxOfDesk: ({
    desk_id,
    page,
    limit,
    search,
  }: {
    desk_id: string | undefined;
    page: number;
    limit: number;
    search: string | undefined;
  }) => {
    const debouncedSearch = useDebounce(search, 500);
    return useQuery({
      queryKey: ["desks", desk_id, "inbox", page, limit, debouncedSearch],
      queryFn: async () => {
        const res = await api.get(`/comms/desks/${desk_id}/inbox`, {
          params: {
            page,
            limit,
            search: debouncedSearch,
          },
        });

        return res.data as T_Api_Success_Res<T_Api_Paginated<T_DesktopInbox>>;
      },
      enabled: Boolean(desk_id),
    });
  },
  useSyncChannel: () => {
    return useMutation({
      mutationKey: ["desks", "channels", "sync"],
      mutationFn: async ({
        desk_id,
        channel_id,
        syncFrom,
        syncUntil,
      }: {
        desk_id: string;
        channel_id: string;
        syncFrom?: string;
        syncUntil?: string;
      }) => {
        const res = await api.post(`/comms/desks/${desk_id}/channels/${channel_id}/sync`, {
          ...(syncFrom && { syncFrom }),
          ...(syncUntil && { syncUntil }),
        });

        return res.data as T_Api_Success_Res<{ queued: boolean }>;
      },
    });
  },
  // useGetInboxOfPerson: ({ person_id }: { person_id: string }) => {
  //   return useQuery({
  //     queryKey: ["persons", person_id, "messages"],
  //     queryFn: async () => {
  //       const res = await api.get(`/comms/persons/${person_id}/messages`);

  //       return res.data as T_Api_Success_Res<T_Api_Paginated<T_Message>>;
  //     },
  //     enabled: !!person_id,
  //   });
  // },
  useGetInboxOfPerson: ({
    person_id,
    enabled,
    selectedChannels,
  }: {
    person_id: string;
    enabled: boolean;
    selectedChannels: string[];
  }) => {
    return useInfiniteQuery({
      queryKey: ["persons", person_id, "messages", selectedChannels],
      queryFn: async ({ pageParam }) => {
        const res = await api.get(`/comms/persons/${person_id}/messages`, {
          params: { page: pageParam, limit: 20, channels: selectedChannels },
        });
        return res.data as T_Api_Success_Res<T_Api_Paginated<T_Message>>;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        const { page, totalPages } = lastPage.result;
        return page < totalPages ? page + 1 : undefined;
      },
      enabled: enabled,
      refetchInterval: 5000,
    });
  },
  useCreateGsuiteChannel: () => {
    return useMutation({
      mutationKey: ["desks", "channels", "gsuite"],
      mutationFn: async ({ desk_id, body }: { desk_id: string; body: T_CreateGsuiteChannelBody }) => {
        const res = await api.post(`/desks/${desk_id}/channels/gsuite`, body);
        return res.data as T_Api_Success_Res<IChannel>;
      },
    });
  },
  useSendMessage: () => {
    return useMutation({
      mutationKey: ["persons", "messages", "send"],
      mutationFn: async ({ person_id, body }: { person_id: string; body: T_SendMessageBody }) => {
        const res = await api.post(`/comms/persons/${person_id}/messages`, body);

        return res.data as T_Api_Success_Res<any>;
      },
    });
  },
  useGetSmsConsent: ({
    personId,
    channelId,
    enabled,
  }: {
    personId: string;
    channelId: string | undefined;
    enabled: boolean;
  }) => {
    return useQuery({
      queryKey: ["sms-consent", personId, channelId],
      queryFn: async () => {
        const res = await api.get(`/comms/persons/${personId}/sms-consent`, {
          params: { channelId },
        });
        return res.data as T_Api_Success_Res<SmsConsentResult>;
      },
      enabled: enabled && !!channelId,
      refetchInterval: false,
    });
  },

  useRequestConsent: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationKey: ["sms-consent", "request"],
      mutationFn: async ({ personId, channelId, number }: { personId: string; channelId: string; number: string }) => {
        const res = await api.post(`/comms/persons/${personId}/sms-consent`, {
          channelId,
          number,
        });
        return res.data as T_Api_Success_Res<SmsConsentResult>;
      },
      onSuccess: (_, variables) => {
        qc.invalidateQueries({ queryKey: ["sms-consent", variables.personId, variables.channelId] });
      },
    });
  },
  useCreatePerson: () => {
    return useMutation({
      mutationKey: ["persons", "create"],
      mutationFn: async ({
        desk_id,
        body,
      }: {
        desk_id: string;
        body: { emails: string[]; numbers: string[]; correlation_id: string; meta?: Record<string, any> };
      }) => {
        const res = await api.post(`/comms/desks/${desk_id}/persons`, body);

        return res.data as T_Api_Success_Res<IPerson>;
      },
    });
  },
  useGetPersonsByDesk: ({ desk_id }: { desk_id: string }) => {
    return useQuery({
      queryKey: ["desks", desk_id, "persons"],
      queryFn: async () => {
        const res = await api.get(`/comms/desks/${desk_id}/persons`);

        return res.data as T_Api_Success_Res<IPerson[]>;
      },
      enabled: !!desk_id,
    });
  },

  useGetPerson: ({ person_id }: { person_id: string | undefined }) => {
    return useQuery({
      queryKey: ["persons-details", person_id],
      queryFn: async () => {
        const res = await api.get(`/comms/persons/${person_id}`);

        return res.data as T_Api_Success_Res<IPerson>;
      },
      enabled: !!person_id,
    });
  },
  useGetThreadsByPerson: ({ person_id, page, limt }: { person_id: string | null; page: number; limt: number }) => {
    return useQuery({
      queryKey: ["persons-thread", person_id, "threads", page, limt],
      queryFn: async () => {
        const res = await api.get(`/comms/persons/${person_id}/threads`, {
          params: {
            page,
            limt,
          },
        });

        return res.data as T_Api_Success_Res<T_Api_Paginated<T_EmailThread>>;
      },
      enabled: !!person_id,
    });
  },
  useUpdatePersonContacts: () => {
    return useMutation({
      mutationFn: async ({
        person_id,
        body,
      }: {
        person_id: string;
        body: { emails?: string[]; numbers?: string[] };
      }) => {
        const res = await api.patch(`/comms/persons/${person_id}/contacts`, body);
        return res.data as T_Api_Success_Res<IPerson>;
      },
    });
  },
  useRemovePersonContacts: () => {
    return useMutation({
      mutationFn: async ({
        person_id,
        body,
      }: {
        person_id: string;
        body: { emails?: string[]; numbers?: string[] };
      }) => {
        const res = await api.delete(`/comms/persons/${person_id}/contacts`, { data: body });
        return res.data as T_Api_Success_Res<IPerson>;
      },
    });
  },
  useBroadcastMessage: () => {
    return useMutation({
      mutationKey: ["persons", "messages", "broadcast"],
      mutationFn: async ({
        person_id,
        body,
      }: {
        person_id: string;
        body: {
          channels: {
            channelId: string;
            contact: string;
            threadId?: string;
          }[];
          html: string;
          subject?: string;
          threadId?: string;
        };
      }) => {
        const res = await api.post(`/comms/persons/${person_id}/messages/broadcast`, body);

        return res.data as T_Api_Success_Res<T_Message[]>;
      },
    });
  },
  useGetMessagesByThreadId: ({ thread_id }: { thread_id: string }) => {
    return useQuery({
      queryKey: ["threads", thread_id, "messages"],
      queryFn: async () => {
        const res = await api.get(`/comms/threads/${thread_id}/messages`);

        return res.data as T_Api_Success_Res<T_Api_Paginated<T_Message>>;
      },
      enabled: !!thread_id,
    });
  },
  useGetPersonByCorrelation: () => {
    return useMutation({
      mutationKey: ["persons", "correlation"],
      mutationFn: async ({ correlationId }: { correlationId: string | undefined }) => {
        const res = await api.post(`/comms/persons/by-correlation/${correlationId}`);

        return res.data as T_Api_Success_Res<{ exists: boolean; person: IPerson }>;
      },
    });
  },
  useCheckDeskCorrelationExists: () => {
    return useMutation({
      mutationKey: ["desks", "correlation", "exists"],
      mutationFn: async ({ correlationId }: { correlationId: string }) => {
        const res = await api.post(`/comms/desks/correlation/${correlationId}/exists`);

        return res.data as T_Api_Success_Res<{ exists: boolean; desk: IDesk }>;
      },
    });
  },
  useFetchDeskInfo: (orgCode: string | undefined) => {
    return useQuery({
      queryKey: ["desks", "correlation_id", orgCode],
      queryFn: async () => {
        const { data } = await api.post<T_Api_Success_Res<{ exists: boolean; desk: DeskDto | null }>>(
          `/comms/desks/correlation/${orgCode}/exists`,
        );
        return data.result;
      },
      enabled: Boolean(orgCode),
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });
  },
};
